import type { UserProfile } from '@/features/user';
import type {
  ApiCompetition,
  ApiParticipant,
  MatchTypeCode,
  ParticipantPayload,
  ParticipantStatusCode,
} from './types';

/**
 * Model widokowy listy zawodników rozgrywki.
 *
 * Jeden rekord `ApiParticipant` to jeden slot: w singlu jedna osoba, w deblu
 * i mikscie para. Ekran pokazuje sloty, bo tak liczy je backend przy limicie
 * miejsc i przy losowaniu.
 */

/** Etykiety statusu zgłoszenia (`ParticipantStatus`). */
export const PARTICIPANT_STATUS_LABELS: Record<ParticipantStatusCode, string> = {
  REGISTERED: 'Dodany',
  ACTIVE: 'W grze',
  ELIMINATED: 'Odpadł',
  WITHDRAWN: 'Wycofany',
};

/** Ton etykiety statusu zgłoszenia. */
export const PARTICIPANT_STATUS_TONE: Record<
  ParticipantStatusCode,
  'success' | 'brand' | 'neutral' | 'danger'
> = {
  REGISTERED: 'success',
  ACTIVE: 'brand',
  ELIMINATED: 'neutral',
  WITHDRAWN: 'danger',
};

/** Czy rozgrywka jest deblowa — wtedy slot wymaga partnera. */
export function isPairCompetition(matchType: MatchTypeCode): boolean {
  return matchType === 'DOUBLES' || matchType === 'MIXT';
}

/**
 * Czy slot zajmuje miejsce w rozgrywce.
 *
 * Odpowiada `CompetitionParticipant.isEligibleForDraw` — wycofani i zablokowani
 * płatniczo zwalniają miejsce, więc nie liczą się do limitu.
 */
export function occupiesSlot(participant: ApiParticipant): boolean {
  return participant.status !== 'WITHDRAWN' && participant.paymentStatus !== 'BLOCKED';
}

/** Liczba zajętych miejsc — ta sama arytmetyka, którą backend stosuje przy limicie. */
export function countOccupiedSlots(participants: readonly ApiParticipant[]): number {
  return participants.filter(occupiesSlot).length;
}

/**
 * Sloty, które faktycznie wejdą do gry — bez listy rezerwowej.
 *
 * Lustrzane odbicie `BracketService.drawBracket`: bierzemy zgłoszenia zajmujące
 * miejsce, porządkujemy je kolejnością zapisów i ucinamy na limicie. Zgłoszenia
 * ponad limit to rezerwa — losowanie ich nie widzi, więc nie może ich widzieć
 * także podgląd losowania ani liczniki zawodników.
 *
 * Rezerwa pokazuje się wyłącznie na ekranie dodawania zawodników, gdzie jest
 * przedmiotem pracy organizatora.
 */
export function playingParticipants(
  participants: readonly ApiParticipant[],
  maxPlayers: number,
): ApiParticipant[] {
  return participants
    .filter(occupiesSlot)
    .sort((a, b) => a.joinedAt.localeCompare(b.joinedAt))
    .slice(0, Math.max(0, maxPlayers));
}

/** Liczba slotów wchodzących do losowania — zajęte miejsca ucięte na limicie. */
export function countPlayingSlots(
  participants: readonly ApiParticipant[],
  maxPlayers: number,
): number {
  return Math.min(countOccupiedSlots(participants), Math.max(0, maxPlayers));
}

/** Zbiera identyfikatory wszystkich osób ze zgłoszeń — do batcha nazwisk. */
export function collectMemberIds(participants: readonly ApiParticipant[]): string[] {
  const ids = new Set<string>();
  for (const participant of participants) {
    ids.add(participant.userId);
    if (participant.partnerId) ids.add(participant.partnerId);
  }
  return [...ids];
}

/** Osoba w slocie — identyfikator i nazwa do wyświetlenia. */
export interface SlotMember {
  userId: string;
  name: string;
}

/** Wiersz tabeli zawodników. */
export interface ParticipantRow {
  participant: ApiParticipant;
  /** Jedna osoba w singlu, dwie w deblu i mikscie. */
  members: SlotMember[];
  /** Nazwa slotu — „Anna Nowak” albo „Anna Nowak / Piotr Wiśniewski”. */
  label: string;
}

/** Buduje mapę `userId → imię i nazwisko` z profili pobranych batchem. */
export function buildProfileNames(users: readonly UserProfile[]): Map<string, string> {
  return new Map(users.map((user) => [user.id, `${user.firstName} ${user.lastName}`.trim()]));
}

/**
 * Składa wiersze tabeli. Nieznane nazwiska zostają jako „…”, żeby lista
 * pokazała się od razu, zanim dojdzie batch profili.
 */
export function buildParticipantRows(
  participants: readonly ApiParticipant[],
  names: ReadonlyMap<string, string>,
): ParticipantRow[] {
  return participants.map((participant) => {
    const ids = participant.partnerId
      ? [participant.userId, participant.partnerId]
      : [participant.userId];
    const members = ids.map((userId) => ({ userId, name: names.get(userId) ?? '…' }));

    return { participant, members, label: members.map((member) => member.name).join(' / ') };
  });
}

/**
 * Porządek listy: kolejność zgłoszeń (`joinedAt`).
 *
 * Pozycja na liście ma znaczenie: przy zgłoszeniach ponad limit gra pierwszych
 * `maxPlayers` slotów, a reszta to lista rezerwowa — dokładnie tak liczy to
 * losowanie po stronie backendu.
 */
export function compareParticipantRows(a: ParticipantRow, b: ParticipantRow): number {
  return a.participant.joinedAt.localeCompare(b.participant.joinedAt);
}

/** Podział listy zgłoszeń względem limitu miejsc. */
export interface ParticipantSections {
  /** Sloty mieszczące się w limicie — wchodzą do losowania. */
  playing: ParticipantRow[];
  /** Sloty ponad limit — lista rezerwowa (wg kolejności zgłoszeń). */
  reserve: ParticipantRow[];
  /** Wycofani i zablokowani płatniczo — nie zajmują miejsc. */
  inactive: ParticipantRow[];
}

/**
 * Dzieli posortowane wiersze na grających, listę rezerwową i nieaktywnych.
 * Lustrzane odbicie losowania: pierwszych `maxPlayers` slotów kwalifikujących
 * się do gry mieści się w limicie, kolejni czekają na zwolnienie miejsca.
 */
export function splitParticipantRows(
  rows: readonly ParticipantRow[],
  maxPlayers: number,
): ParticipantSections {
  const eligible = rows.filter((row) => occupiesSlot(row.participant));
  return {
    playing: eligible.slice(0, Math.max(0, maxPlayers)),
    reserve: eligible.slice(Math.max(0, maxPlayers)),
    inactive: rows.filter((row) => !occupiesSlot(row.participant)),
  };
}

/**
 * Dzieli listę zgłoszeń po losowaniu — na podstawie tego, kto realnie wszedł
 * do gry (czyli występuje w wylosowanych meczach).
 *
 * Po losowaniu nie ma już listy rezerwowej: zgłoszeń ponad limit losowanie nie
 * wzięło i nikogo nie da się dopisać do rozlosowanego turnieju, więc znikają
 * z widoku zamiast czekać na zwolnienie miejsca. Rezygnacja wylosowanego to
 * scratch — trafia do nieaktywnych i **nie** wciąga nikogo w swoje miejsce.
 *
 * Dlatego o przynależności decydują mecze, a nie pozycja na liście: przy
 * dzieleniu po pozycji wycofanie się gracza po cichu awansowało rezerwowego
 * do „grających”, choć nie miał ani jednego meczu.
 */
export function splitDrawnParticipantRows(
  rows: readonly ParticipantRow[],
  drawnMemberIds: ReadonlySet<string>,
): ParticipantSections {
  const drawn = rows.filter((row) =>
    row.members.some((member) => drawnMemberIds.has(member.userId)),
  );

  return {
    playing: drawn.filter((row) => occupiesSlot(row.participant)),
    reserve: [],
    inactive: drawn.filter((row) => !occupiesSlot(row.participant)),
  };
}

/** Filtruje wiersze po nazwisku. */
export function filterParticipantRows(
  rows: readonly ParticipantRow[],
  search: string,
): ParticipantRow[] {
  const needle = search.trim().toLocaleLowerCase('pl-PL');
  if (!needle) return [...rows];
  return rows.filter((row) => row.label.toLocaleLowerCase('pl-PL').includes(needle));
}

/* --- Dodawanie zawodnika --- */

/** Sposób wskazania osoby: istniejące konto albo zaproszenie gościa. */
export type MemberMode = 'existing' | 'guest';

/** Formularz jednej strony slotu. */
export interface MemberDraft {
  mode: MemberMode;
  /** Wybrany gracz z wyszukiwarki — tylko w trybie `existing`. */
  userId: string | null;
  email: string;
  firstName: string;
  lastName: string;
  gender: 'MALE' | 'FEMALE';
}

/** Pusta strona slotu. */
export const EMPTY_MEMBER: MemberDraft = {
  mode: 'existing',
  userId: null,
  email: '',
  firstName: '',
  lastName: '',
  gender: 'MALE',
};

/**
 * Człony adresu podstawianego gościowi, dla którego organizator nie podał e-maila.
 *
 * Backend zakłada gościowi konto po adresie i ODRZUCA adres już zajęty
 * (`CompetitionService.resolveMember` → „ma już konto w Tenify"), więc jeden
 * wspólny `test@tenify.pl` przeszedłby wyłącznie dla pierwszego takiego
 * zawodnika. Dlatego każdy dostaje własny wariant tego samego adresu.
 */
const FALLBACK_EMAIL_LOCAL_PART = 'test';
const FALLBACK_EMAIL_DOMAIN = 'tenify.pl';

/**
 * Buduje adres zastępczy dla gościa bez e-maila: `test+<znacznik>@tenify.pl`.
 *
 * `user-service` porównuje adresy dosłownie (`findByEmail`), więc warianty po
 * `+` to osobne konta. Znacznik składa się ze stempla czasu i losowego ogona —
 * dwa sloty dodane w tej samej milisekundzie też dostaną różne adresy.
 * Wspólny prefiks `test+` pozwala później odróżnić te konta od prawdziwych.
 */
export function fallbackGuestEmail(): string {
  const tag = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
  return `${FALLBACK_EMAIL_LOCAL_PART}+${tag}@${FALLBACK_EMAIL_DOMAIN}`;
}

/**
 * Sprawdza jedną stronę slotu. Zwraca komunikat albo `null`.
 *
 * Odpowiada `ParticipantSpec.validate` po stronie serwera: albo `userId`,
 * albo komplet danych gościa — nigdy jedno i drugie.
 */
export function validateMember(member: MemberDraft, label: string): string | null {
  if (member.mode === 'existing') {
    return member.userId ? null : `Wybierz ${label} z listy graczy.`;
  }
  // E-mail jest nieobowiązkowy — pusty podstawia `toParticipantPayload`.
  // Wpisany musi być poprawny, żeby literówka nie założyła konta pod złym adresem.
  if (member.email.trim() && !member.email.includes('@')) {
    return `Podaj poprawny adres e-mail dla ${label}.`;
  }
  if (!member.firstName.trim() || !member.lastName.trim()) {
    return `Podaj imię i nazwisko dla ${label}.`;
  }
  return null;
}

/**
 * Sprawdza cały formularz dodawania slotu.
 *
 * Reguły płci odwzorowują `GenderValidationService`: mikst to para kobieta +
 * mężczyzna, debel to para tej samej płci. Sprawdzamy je tylko wtedy, gdy obie
 * strony są gośćmi — płci istniejących graczy nie znamy bez dodatkowego zapytania,
 * więc te przypadki rozstrzyga backend.
 */
export function validateSlot(
  player: MemberDraft,
  partner: MemberDraft | null,
  matchType: MatchTypeCode,
): string | null {
  const playerProblem = validateMember(player, 'zawodnika');
  if (playerProblem) return playerProblem;

  if (!isPairCompetition(matchType)) return null;

  if (!partner) return 'Ta rozgrywka wymaga pary — dodaj partnera.';
  const partnerProblem = validateMember(partner, 'partnera');
  if (partnerProblem) return partnerProblem;

  if (
    player.mode === 'existing' &&
    partner.mode === 'existing' &&
    player.userId === partner.userId
  ) {
    return 'Partner musi być innym graczem.';
  }

  if (player.mode === 'guest' && partner.mode === 'guest') {
    if (matchType === 'MIXT' && player.gender === partner.gender) {
      return 'Mikst wymaga pary kobieta + mężczyzna.';
    }
    if (matchType === 'DOUBLES' && player.gender !== partner.gender) {
      return 'Debel wymaga pary tej samej płci.';
    }
  }

  return null;
}

/** Zamienia formularz strony slotu na ładunek dla API. */
export function toParticipantPayload(member: MemberDraft): ParticipantPayload {
  if (member.mode === 'existing') {
    // Pole zostawiamy pominięte, a nie ustawione na `undefined` — backend
    // odrzuca ładunek, w którym stoi i `userId`, i dane gościa.
    return member.userId ? { userId: member.userId } : {};
  }
  return {
    // Organizator może pominąć e-mail — backend wymaga go zawsze, więc
    // podstawiamy adres zastępczy, nie pokazując tego w formularzu.
    email: member.email.trim() || fallbackGuestEmail(),
    firstName: member.firstName.trim(),
    lastName: member.lastName.trim(),
    gender: member.gender,
  };
}

/* --- Stan ekranu --- */

/** Powód, dla którego nie można teraz dodawać zawodników. */
export type AddBlockReason = 'not-open' | null;

/**
 * Czy da się dodać kolejny slot.
 *
 * Backend przyjmuje zgłoszenia przy otwartych zapisach — także ponad limit
 * miejsc: nadmiarowe sloty trafiają na listę rezerwową, więc pełna lista
 * nie blokuje dodawania.
 */
export function addBlockReason(competition: ApiCompetition | undefined): AddBlockReason {
  if (!competition) return 'not-open';
  if (competition.status !== 'REGISTRATION_OPEN') return 'not-open';
  return null;
}

/** Czy zgłoszenia można jeszcze usuwać — backend pozwala tylko przed startem. */
export function canRemoveParticipants(competition: ApiCompetition | undefined): boolean {
  if (!competition) return false;
  return (
    competition.status === 'DRAFT' ||
    competition.status === 'AWAITING_PAYMENT' ||
    competition.status === 'REGISTRATION_OPEN' ||
    competition.status === 'REGISTRATION_CLOSED'
  );
}
